import CalendarUtil from "../utils/CalendarUtil";
import common from "../../../utils/common";
import { CloseButton } from "react-bootstrap";
import { CSSProperties, ReactNode } from "react";
import { EventContentArg } from "@fullcalendar/core/index.js";
import { ButtonDropdownMenuOption } from "../../ButtonDropDownMenu/ButtonDropDownMenu";
import { ColorUtils } from "../../../utils/ColorUtils";
import tinycolor from "tinycolor2";

type EventElementProp = {
  title?: string;
  time?: string;
  backgroundColor?: string;
  closeButton?: ReactNode;
  classNames?: string;
};

const getDayEventStyle = (backgroundColor?: string) => {
  return {
    backgroundColor: backgroundColor
      ? tinycolor(backgroundColor).setAlpha(0.7).toRgbString()
      : `transparent`, //info.event.backgroundColor ??
    width: "100%",
    padding: "2px",
    color: ColorUtils.getTextColor(backgroundColor || "#000"),
    borderRadius: "5px",
  } as CSSProperties;
};

function HolidayEvent({ title }: EventElementProp) {
  return (
    <div
      style={{
        position: "absolute",
        top: "0px",
        // backgroundColor: "#ff5733",
        padding: "0 5px ",
        margin: 0,
        fontSize: "13px",
        color: "#3d3d3d",
        maxWidth: "160px",
        wordBreak: "break-word",
        wordWrap: "break-word",
        whiteSpace: "normal",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {title}
    </div>
  );
}

function FullDayEvent({
  title,
  closeButton,
  backgroundColor,
  classNames,
}: EventElementProp) {
  return (
    <div style={getDayEventStyle(backgroundColor)} className={classNames}>
      &nbsp;
      <b>
        <u>{title}</u>
      </b>
      {closeButton}
    </div>
  );
}

function NormalEvent({
  title,
  time,
  backgroundColor,
  closeButton,
  classNames,
}: EventElementProp) {
  return (
    <div style={getDayEventStyle(backgroundColor)} className={classNames}>
      <b>{time}</b>
      &nbsp;
      <span>{title}</span>
      {closeButton}
    </div>
  );
}

type CustCloseButtonProp = {
  info: EventContentArg;
  buttonClick: (event: any, info: EventContentArg) => void;
};

function CustCloseButton({ info, buttonClick }: CustCloseButtonProp) {
  return (
    <CloseButton
      style={{
        marginTop: "3px",
        fontSize: "0.55rem",
        position: "absolute",
        top: 3,
        right: 4,
      }}
      onClick={(e) => {
        e.preventDefault();
        if (typeof buttonClick === "function") {
          buttonClick(e, info);
        }
      }}
    />
  );
}

function EventRender(
  info: EventContentArg,
  calendarData: ButtonDropdownMenuOption[],
  selectedCalendars: number[],
  buttonClick: (event: MouseEvent, info: EventContentArg) => ReactNode | void,
) {
  if (!info.event.start) {
    return;
  }

  const calendar_id =
    typeof info.event?.extendedProps?.calendarIds === "number"
      ? Number(info.event?.extendedProps?.calendarIds)
      : Array.isArray(info.event?.extendedProps?.calendarIds) &&
          info.event?.extendedProps?.calendarIds.length > 0
        ? Number(info.event?.extendedProps?.calendarIds[0])
        : -1;
  const selected = selectedCalendars.includes(calendar_id);
  const calendar = calendarData.find((x) => x.value === calendar_id);
  const pastEventClassName =
    info?.event?.end !== null && info?.event?.end < new Date()
      ? "past-event"
      : "";
  const classNames = selected ? `${pastEventClassName}` : "hide";
  const eventColor = calendar !== null ? calendar?.color : undefined;

  const isHolidayEvent = CalendarUtil.isHolidayEvent(info.event);
  if (isHolidayEvent) {
    return <HolidayEvent title={info.event.title} />;
  }

  const isFullDay =
    info.event.allDay ||
    common.diffDays(info.event.start, info.event.end ?? "") === 1;

  const timeText = !isFullDay
    ? CalendarUtil.getTimeTextString(info.event.start, info.event.end ?? "")
    : "";
  const titleText = CalendarUtil.transformEventTitleText(
    timeText,
    " ",
    info.event.title,
  );

  const closeButton = <CustCloseButton info={info} buttonClick={buttonClick} />;
  if (isFullDay) {
    return (
      <FullDayEvent
        title={titleText}
        closeButton={closeButton}
        backgroundColor={eventColor}
        classNames={classNames}
      />
    );
  }
  return (
    <NormalEvent
      title={titleText}
      time={timeText}
      closeButton={closeButton}
      backgroundColor={eventColor}
      classNames={classNames}
    />
  );
}

const CalendarEventRender = {
  EventRender,
};

export default CalendarEventRender;
